import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSessionClaims } from '@/auth/session-claims';
import { alertLine } from '@/components/alerts/alert-line';
import { ME_QUERY_KEY, type OperatorMe } from '@/lib/me/api';
import { queryClient } from '@/lib/query-client';
import { alertTarget, readAlert } from '@/state/notifications.store';
import { watchOsNotify } from './os-notify';

// Mounted once in the AppShell, beside the alert host: wires `watchOsNotify` to the
// console — the alert's concrete line (U1's `alertLine`, «Cancelado H2 1821 · EZE → SCL ·
// 22:10») as the title, «y N más» as the body of a burst; a click reads that alert (like
// a click in the bell) and goes to U1's target. Renders nothing.

export function OsNotifyBridge(): null {
  const navigate = useNavigate();
  const { t } = useTranslation('alerts');
  const claimed = useSessionClaims()?.operatorUrn ?? undefined;
  const live = useRef({ navigate, t, claimed });
  useEffect(() => {
    live.current = { navigate, t, claimed };
  }, [navigate, t, claimed]);

  useEffect(
    () =>
      watchOsNotify({
        say: (lead, more) => ({
          title: alertLine(live.current.t, lead),
          body: more > 0 ? live.current.t('more', { count: more }) : '',
        }),
        go: (lead) => {
          readAlert(lead.id);
          const to = alertTarget(lead);
          if (to) void live.current.navigate(to);
        },
        me: () =>
          queryClient.getQueryData<OperatorMe>(ME_QUERY_KEY)?.operatorUrn ?? live.current.claimed,
      }),
    [],
  );
  return null;
}
